import { Component, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import {
  merge,
  startWith,
  switchMap,
  catchError,
  map,
  of,
  Observable,
} from 'rxjs';
import { PageEvent } from '@angular/material/paginator';

import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { ProjectService } from '../../services/project.service';
import { UsersService } from '../../services/users.service';
import { ActivatedRoute, Router } from '@angular/router';
import { applyCenterScopeToWhere } from 'src/app/utils/tenant-scope.util';
import { PermissionService } from '../../services/permission.service';

@Component({
  selector: 'app-project',
  templateUrl: './project.component.html',
  styleUrls: ['./project.component.scss'],
})
export class ProjectComponent {
  /* ---------------------- Form & Filters -------------------------- */
  searchQuery = new FormControl();
  dateQuery = new FormControl();

  /* ---------------------- Table Essentials -------------------------- */
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  dataSource: MatTableDataSource<any> = new MatTableDataSource();
  totalRows = 0;
  projectCategories = [
    'Software Development',
    'Web Development',
    'Mobile Development',
    'System Integration',
    'DevOps / Infrastructure',
    'IT Support / Maintenance',
    'Cloud Implementation',
    'Cybersecurity',
    'Data & Analytics',
    'AI / Machine Learning',
    'API Development',
    'Business Process Improvement',
    'Operations Optimization',
    'Automation',
    'Compliance / Governance',
    'Product Design',
    'Product Enhancement',
    'Prototype / MVP',
    'Research & Development (R&D)',
    'Digital Marketing',
    'Branding',
    'UI/UX Design',
    'Graphic Design',
    'CRM Customization',
    'ERP Module',
    'Finance Module',
    'HR Module',
    'Inventory Module',
    'Support / Bug Fixing',
    'Retainer Support',
    'Training / Enablement',
    'Healthcare Projects',
    'Education Projects',
    'E-Commerce Projects',
    'Logistics Projects',
    'Manufacturing Projects',
  ];
  /* ---------------------- UI State -------------------------- */
  initialLoad = true;
  reloading = false;
  pageSize = 25;
  pageSizeOptions = [10, 25, 50, 100];
  private sortPageResetBound = false;
  private searchPageResetBound = false;
  private listLoadBound = false;

  private readonly categoryFilterKeyToLabel: Record<string, string> = {
    software_development: 'Software Development',
    web_development: 'Web Development',
    mobile_development: 'Mobile Development',
    system_integration: 'System Integration',
    devops_infrastructure: 'DevOps / Infrastructure',
    it_support_maintenance: 'IT Support / Maintenance',
    cloud_implementation: 'Cloud Implementation',
    cybersecurity: 'Cybersecurity',
    data_analytics: 'Data & Analytics',
    ai_machine_learning: 'AI / Machine Learning',
    api_development: 'API Development',
    business_process_improvement: 'Business Process Improvement',
    operations_optimization: 'Operations Optimization',
    automation: 'Automation',
    compliance_governance: 'Compliance / Governance',
  };

  formAction = 'Add';
  selectedImage: any;
  btnLoading: boolean = false;
  /* ---------------------- Edit / Delete -------------------------- */
  editData: any;
  deleteData: any;

  /* ---------------------- User & Manager Cache -------------------------- */
  users: any = [];

  /* ---------------------- Columns -------------------------- */
  displayedColumns: string[] = [
    'project_prefix',
    'name',
    'client_name',
    'project_manager',
    'status',
    'priority',
    'start_date',
    'planned_end_date',
    'progress',
    'bugs',
    'action',
  ];

  constructor(
    private localStorage: LocalStorageService,
    private permissionService: PermissionService,
    private api: ProjectService,
    private userApi: UsersService,
    private modalService: NgbModal,
    private toast: ToastrService,
    private router: Router,
  ) {}

  filterArray: any[] = [];

  buildFilterArray() {
    const managerOptions = (this.users || []).map((u: any) => ({
      value:
        `${u.prefix ? u.prefix + ' ' : ''}${u.fname || ''} ${u.lname || ''}`.trim() ||
        u.email,
      key: u.user_id,
    }));

    this.filterArray = [
      { key: 'name', value: 'Project Name' },
      { key: 'project_prefix', value: 'Project Prefix' },
      {
        key: 'project_manager_id',
        value: 'Project Manager',
        type: 'select',
        options: managerOptions,
      },
      {
        key: 'status',
        value: 'Status',
        type: 'select',
        options: [
          { value: 'Not Started', key: 'not_started' },
          { value: 'In Progress', key: 'in_progress' },
          { value: 'On Hold', key: 'on_hold' },
          { value: 'Delayed', key: 'delayed' },
          { value: 'Cancelled', key: 'cancelled' },
          { value: 'Completed', key: 'completed' },
        ],
      },
      {
        key: 'priority',
        value: 'Priority',
        type: 'select',
        options: [
          { value: 'Low', key: 'low' },
          { value: 'Medium', key: 'medium' },
          { value: 'High', key: 'high' },
          { value: 'Urgent', key: 'urgent' },
          { value: 'Critical', key: 'critical' },
        ],
      },
      {
        key: 'category',
        value: 'Project Category',
        type: 'select',
        options: [
          { value: 'Software Development', key: 'software_development' },
          { value: 'Web Development', key: 'web_development' },
          { value: 'Mobile Development', key: 'mobile_development' },
          { value: 'System Integration', key: 'system_integration' },
          { value: 'DevOps / Infrastructure', key: 'devops_infrastructure' },
          { value: 'IT Support / Maintenance', key: 'it_support_maintenance' },
          { value: 'Cloud Implementation', key: 'cloud_implementation' },
          { value: 'Cybersecurity', key: 'cybersecurity' },
          { value: 'Data & Analytics', key: 'data_analytics' },
          { value: 'AI / Machine Learning', key: 'ai_machine_learning' },
          { value: 'API Development', key: 'api_development' },
          {
            value: 'Business Process Improvement',
            key: 'business_process_improvement',
          },
          { value: 'Operations Optimization', key: 'operations_optimization' },
          { value: 'Automation', key: 'automation' },
          { value: 'Compliance / Governance', key: 'compliance_governance' },
        ],
      },
    ];
  }
  permissions: any;

  /* -------------------------------------------------------------
     INIT
  --------------------------------------------------------------*/
  ngOnInit(): void {
    this.buildFilterArray();
    this.getUsers();
  }
  openReportManager(project: any) {
    const projectId = project?.id || project?.project_id;
    this.router.navigate(['/main/manage-project-reports', projectId]);
  }
  ngAfterViewInit() {
    if (this.paginator) {
      this.paginator.pageSize = this.pageSize;
    }
    this.permissions = this.permissionService.getAccessObject('project');
    this.loadData();
  }

  private buildProjectListParams(): Record<string, unknown> {
    const pageIndex = this.paginator?.pageIndex ?? 0;
    const pageSize = this.paginator?.pageSize ?? this.pageSize;

    const param: Record<string, unknown> = {
      page: pageIndex * pageSize,
      limit: pageSize,
      user_id: this.localStorage.getUserId(),
      where: {
        client_id: this.localStorage.getClientId(),
      },
      order: {
        col: '"project"."updated_at"',
        dir: 'DESC',
      },
    };

    const query = this.searchQuery.value;
    if (query && typeof query === 'object' && Object.keys(query).length > 0) {
      const filter: Record<string, unknown> = { ...query };
      if (
        filter['category'] &&
        this.categoryFilterKeyToLabel[String(filter['category'])]
      ) {
        filter['category'] =
          this.categoryFilterKeyToLabel[String(filter['category'])];
      }
      param['filter'] = filter;
    } else if (typeof query === 'string' && query.trim()) {
      param['filter'] = query.trim();
    }

    return param;
  }

  private fetchProjectListPage$(): Observable<any> {
    return this.api
      .getAllProject(this.buildProjectListParams())
      .pipe(catchError(() => of(null)));
  }

  private normalizeProjectListResponse(response: any): any[] {
    this.initialLoad = false;
    this.reloading = false;

    if (!response) {
      this.totalRows = 0;
      return [];
    }

    const count = response.count;
    this.totalRows =
      typeof count === 'number'
        ? count
        : Array.isArray(count)
          ? count.length
          : Number(count) || 0;

    return response.rows ?? [];
  }

  private applyProjectRows(rows: any[]): void {
    this.dataSource = new MatTableDataSource<any>(rows);
    // Server-side pagination: MatTableDataSource must not slice the current page again.
    this.dataSource.paginator = null;
  }

  onPaginatorPage(event: PageEvent): void {
    this.pageSize = event.pageSize;
  }

  /* -------------------------------------------------------------
     LOAD MANAGERS (project_manager_id)
  --------------------------------------------------------------*/
  getUsers() {
    let param = { client_id: this.localStorage.getClientId() };

    this.userApi.findAllUsers(param).subscribe({
      next: (res) => {
        this.users = res || [];
        this.buildFilterArray();
      },
      error: (err) => {
        console.error(err);
        this.buildFilterArray();
      },
    });
  }

  /* -------------------------------------------------------------
     LOAD PROJECT DATA (Pagination + Filter + Sort)
  --------------------------------------------------------------*/
  loadData() {
    if (!this.paginator) {
      return;
    }

    try {
      if (!this.sortPageResetBound && this.sort) {
        this.sortPageResetBound = true;
        this.sort.sortChange.subscribe(() => {
          this.paginator.pageIndex = 0;
        });
      }

      if (!this.searchPageResetBound) {
        this.searchPageResetBound = true;
        this.searchQuery.valueChanges.subscribe(() => {
          this.paginator.pageIndex = 0;
        });
      }

      if (this.listLoadBound) {
        return;
      }
      this.listLoadBound = true;

      merge(
        this.searchQuery.valueChanges,
        this.sort?.sortChange ?? of(null),
        this.paginator.page,
      )
        .pipe(
          startWith({}),
          switchMap(() => {
            this.reloading = true;
            return this.fetchProjectListPage$();
          }),
          map((response) => this.normalizeProjectListResponse(response)),
        )
        .subscribe({
          next: (rows) => this.applyProjectRows(rows),
          error: (e) => {
            console.error(e);
            this.reloading = false;
            this.initialLoad = false;
          },
        });
    } catch (e) {
      console.error(e);
      this.initialLoad = false;
      this.reloading = false;
    }
  }

  fnChangeTab(tab: any) {}
  /* -------------------------------------------------------------
     RELOAD DATA
  --------------------------------------------------------------*/
  reloadData() {
    this.reloading = true;

    this.fetchProjectListPage$()
      .pipe(map((response) => this.normalizeProjectListResponse(response)))
      .subscribe({
        next: (rows) => this.applyProjectRows(rows),
        error: (e) => {
          console.error(e);
          this.reloading = false;
        },
      });
  }

  /* -------------------------------------------------------------
     OPEN MODALS
  --------------------------------------------------------------*/
  open(content: any) {
    this.formAction = 'Add';
    this.editData = {};
    this.modalService.open(content, {
      size: 'lg',
      centered: true,
      backdrop: 'static',
      scrollable: true,
    });
  }

  openEdit(content: any, data: any) {
    this.formAction = 'Update';
    this.editData = data;

    this.modalService.open(content, {
      size: 'lg',
      centered: true,
      backdrop: 'static',
      scrollable: true,
    });
  }
  viewDetails(project: any) {
    const projectId = project?.id || project?.project_id;
    this.router.navigate(['/main/project-detail', projectId]);
  }
  openTaskManager(project: any) {
    const projectId = project?.id || project?.project_id;
    this.router.navigate(['/main/manage-project-tasks', projectId]);
  }
  openBugManager(project: any) {
    const projectId = project?.id || project?.project_id;
    this.router.navigate(['/main/manage-bugs', projectId]);
  }

  openDeleteEvent(content: any, data: any) {
    this.deleteData = data;

    this.modalService.open(content, {
      size: 'md',
      centered: true,
      backdrop: 'static',
    });
  }

  handleSubmitProject(payload: any) {
    console.log('payload', payload);
    this.btnLoading = true;
    if (!payload) {
      this.btnLoading = false;
      return;
    } else {
      if (this.formAction === 'Update') {
        payload.project_id = this.editData.project_id;
        console.log('Update Payload', payload);
        this.api.updateProject(payload).subscribe({
          next: (res) => {
            console.log('Project Updated', res);
            this.btnLoading = false;
            this.toast.success('Project updated successfully');
            this.reloadData();
            this.modalService.dismissAll();
          },
          error: (e) => {
            this.btnLoading = false;
            // Show error message if available
            const errorMsg = e?.error?.message || 'Failed to update project';
            this.toast.error(errorMsg);
            console.error('Update error:', e);
          },
        });
        return;
      }
      console.log('Create Payload', payload);
      this.api.createProject(payload).subscribe({
        next: (res) => {
          console.log('Project Created', res);
          this.btnLoading = false;
          this.toast.success('Project created successfully');
          this.reloadData();
          this.modalService.dismissAll();
        },
        error: (e) => {
          this.btnLoading = false;
          // Show error message if available
          const errorMsg = e?.error?.message || 'Failed to create project';
          this.toast.error(errorMsg);
          console.error('Create error:', e);
        },
      });
    }
  }
  /* -------------------------------------------------------------
     DELETE PROJECT
  --------------------------------------------------------------*/
  handleClosDialog() {
    this.btnLoading = false; // Reset loading state
    this.modalService.dismissAll();
  }
  openDelete(data: any, modal: any) {
    this.editData = data;
    this.modalService.open(modal, {
      size: 'md',
      centered: true,
      backdrop: 'static',
      scrollable: true,
    });
  }
  prettyAction(type: string) {
    return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
  confirmDelete() {
    if (!this.editData || !this.editData.project_id) return;
    this.btnLoading = true;
    this.api.deleteProject({ project_id: this.editData.project_id }).subscribe({
      next: (res: any) => {
        this.btnLoading = false;
        this.modalService.dismissAll();
        this.reloadData();
      },
      error: (e: any) => {
        this.btnLoading = false;
        console.error(e);
      },
    });
  }
  handleDeleteProject() {
    const params = { id: this.deleteData.id };

    this.api.deleteProject(params).subscribe({
      next: (res: any) => {
        this.toast.success('Project deleted successfully');
        this.reloadData();
        this.modalService.dismissAll();
      },
      error: (err) => console.error(err),
    });
  }

  /* -------------------------------------------------------------
     SEARCH FILTER
  --------------------------------------------------------------*/
  searchFilter(query: any) {
    console.log('query', query);
    this.searchQuery.setValue(query);
  }

  /* -------------------------------------------------------------
     IMAGE PREVIEW
  --------------------------------------------------------------*/
  showImage(content: any, image: string) {
    this.selectedImage = image;

    this.modalService.open(content, {
      size: 'lg',
      centered: true,
    });
  }
}
